import { Helmet } from 'react-helmet';
import { resolveImageUrl } from '../../lib/utils';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Building2, Search, Trash2, Pencil, CheckCircle, Home, SlidersHorizontal, RefreshCw, DollarSign, Eye, RotateCcw, X, Phone, Calendar
} from 'lucide-react';
import { getProperties, updateProperty, deleteProperty, markPropertySold, updatePropertyStatus, getAppUsers, getAreas, addTenant, getPgOccupants, addPgBedOccupant, vacatePgBed } from '../../lib/apiService';
import { API_BASE } from '../../lib/apiConfig';
import PropertyDetail from '../../components/PropertyDetail';
import ReactSelect from 'react-select';

const fmt = (n) => {
  const v = Number(n) || 0;
  if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`;
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 border border-emerald-300',
  inactive: 'bg-slate-100 text-slate-600 border border-slate-300',
  sold: 'bg-red-50 text-red-600 border border-red-300',
  rented: 'bg-blue-50 text-blue-600 border border-blue-300',
};

const LISTING_BADGE = {
  sell: 'bg-orange-50 text-orange-700 border border-orange-200',
  rent: 'bg-cyan-50 text-cyan-700 border border-cyan-200',
};

const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar',
  'Chandigarh', 'Chhattisgarh', 'Dadra and Nagar Haveli & Daman and Diu', 'Delhi', 'Goa',
  'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir', 'Jharkhand', 'Karnataka',
  'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim',
  'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal'
];

const PROPERTY_TYPES = ['Flat', 'House', 'Plot', 'Building', 'Appartment', 'PG'];
const BHK_OPTIONS = ['1RK', '2RK', '3RK', '4RK', '5RK', '5+RK', '1BHK', '2BHK', '3BHK', '4BHK', '5BHK', '5+BHK'];
const FURNISHING_OPTIONS = ['Unfurnished', 'Semi-Furnished', 'Fully Furnished'];
const PRICE_RANGE_OPTIONS = [
  { label: '₹0 - ₹4,999', value: '0-4999' },
  { label: '₹5,000 - ₹10,000', value: '5000-10000' },
  { label: '₹10,000 - ₹15,000', value: '10000-15000' },
  { label: '₹15,000 & above', value: '15000+' }
];

const PropertiesPage = () => {
  const { userProfile } = useAuth();
  const role = userProfile?.role;
  const navigate = useNavigate();

  const [properties, setProperties] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    property_type: '',
    listing_type: '',
    status: '',
    bhk: '',
    furnishing: '',
    price_range: '',
    serves_food: '',
    state: 'Nagaland',
    district: '',
    city: ''
  });
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempFilters, setTempFilters] = useState({
    property_type: '',
    listing_type: '',
    status: '',
    bhk: '',
    furnishing: '',
    price_range: '',
    serves_food: '',
    state: 'Nagaland',
    district: '',
    city: ''
  });
  const [districtsList, setDistrictsList] = useState([]);

  // Modals / State
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [markSoldModal, setMarkSoldModal] = useState(null);
  const [soldToType, setSoldToType] = useState('');
  const [soldTo, setSoldTo] = useState('');
  const [tenantPhone, setTenantPhone] = useState('');
  const [tenantEmail, setTenantEmail] = useState('');
  const [moveInDate, setMoveInDate] = useState('');
  const [markingSold, setMarkingSold] = useState(false);

  // PG Bed-wise management
  const [pgBedModal, setPgBedModal] = useState(null); // Property obj
  const [pgOccupants, setPgOccupants] = useState([]);
  const [loadingPgBeds, setLoadingPgBeds] = useState(false);
  const [assigningBed, setAssigningBed] = useState(null); // { roomId, bedNumber }
  const [bedAssignType, setBedAssignType] = useState('');
  const [bedAssignName, setBedAssignName] = useState('');
  const [bedAssignPhone, setBedAssignPhone] = useState('');
  const [bedAssignEmail, setBedAssignEmail] = useState('');
  const [bedAssignMoveIn, setBedAssignMoveIn] = useState('');
  const [bedAssigning, setBedAssigning] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [appUsers, setAppUsers] = useState([]);

  const fetchProperties = useCallback(async () => {
    if (!userProfile) return;
    setLoading(true);
    try {
      const res = await getProperties({ page, filters: { ...filters, search } });
      setProperties(res.properties || []);
      setTotal(res.total || 0);
      setPages(res.pages || 1);
    } catch (err) {
      toast.error(err.message || 'Failed to load properties');
    } finally {
      setLoading(false);
    }
  }, [userProfile, page, filters, search]);

  useEffect(() => {
    fetchProperties();
    getAppUsers().then(res => {
      if (res?.status) setAppUsers(res.users || []);
    }).catch(console.error);
  }, [fetchProperties]);

  useEffect(() => {
    getAreas(tempFilters.state || 'Nagaland')
      .then(res => {
        if (res?.status) {
          const list = (res.areas || []).map(a => a.district).filter(Boolean);
          setDistrictsList([...new Set(list)].sort());
        } else {
          setDistrictsList([]);
        }
      })
      .catch(() => setDistrictsList([]));
  }, [tempFilters.state]);

  const handleOpenFilterModal = () => {
    setTempFilters({ ...filters });
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    setFilters({ ...tempFilters });
    setPage(1);
    setIsFilterModalOpen(false);
  };

  const handleResetTempFilters = () => {
    setTempFilters({
      property_type: '',
      listing_type: '',
      status: '',
      bhk: '',
      furnishing: '',
      price_range: '',
      serves_food: '',
      state: 'Nagaland',
      district: '',
      city: ''
    });
  };

  const handleResetFilters = () => {
    const defaultState = {
      property_type: '',
      listing_type: '',
      status: '',
      bhk: '',
      furnishing: '',
      price_range: '',
      serves_food: '',
      state: 'Nagaland',
      district: '',
      city: ''
    };
    setFilters(defaultState);
    setTempFilters(defaultState);
    setPage(1);
  };

  const handleRemoveFilter = (key) => {
    setFilters(prev => ({ ...prev, [key]: '' }));
    setPage(1);
  };

  const activeFiltersCount = Object.values(filters).filter(Boolean).length;
  const tempActiveCount = Object.values(tempFilters).filter(Boolean).length;

  const handleSearchSubmit = (e) => { e.preventDefault(); setPage(1); fetchProperties(); };

  // Edit (redirect to dataentry/AddProperty with parameter id)
  const openEdit = (prop) => {
    navigate(`/edit-property/${prop.id}`);
  };

  // Delete
  const handleDelete = async (prop) => {
    if (!window.confirm(`Delete "${prop.property_title}"? This cannot be undone.`)) return;
    setDeletingId(prop.id);
    try {
      await deleteProperty(prop.id);
      toast.success('Property deleted');
      fetchProperties();
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  // Mark Sold/Rented
  const handleMarkSold = async () => {
    if (!markSoldModal) return;
    setMarkingSold(true);
    try {
      let finalSoldTo = '';
      let selectedUser = null;
      if (soldToType === 'others') {
        finalSoldTo = soldTo;
      } else if (soldToType) {
        selectedUser = appUsers.find(x => String(x.id) === String(soldToType));
        if (selectedUser) finalSoldTo = `${selectedUser.name} (${selectedUser.phone})`;
      }

      if (markSoldModal.listing_type === 'rent') {
        const formData = new FormData();
        formData.append('property_id', markSoldModal.id);

        if (selectedUser) {
          formData.append('tenant_user_id', selectedUser.id);
        } else {
          formData.append('tenant_name', soldTo);
          formData.append('tenant_phone', tenantPhone);
          formData.append('tenant_email', tenantEmail);
        }

        if (moveInDate) {
          formData.append('move_in_date', moveInDate);
        }

        await addTenant(formData);
        toast.success('Property marked as rented');
      } else {
        if (soldToType === 'others') {
          let details = [];
          if (tenantPhone) details.push(tenantPhone);
          if (tenantEmail) details.push(tenantEmail);
          let extraStr = details.length > 0 ? ` (${details.join(', ')})` : '';
          let moveInStr = moveInDate ? ` [Move-in: ${moveInDate}]` : '';
          finalSoldTo = `${soldTo}${extraStr}${moveInStr}`;
        } else if (selectedUser && moveInDate) {
          finalSoldTo = `${finalSoldTo} [Move-in: ${moveInDate}]`;
        }
        await markPropertySold(markSoldModal.id, finalSoldTo, markSoldModal.listing_type);
        toast.success('Property marked as sold');
      }

      setMarkSoldModal(null);
      setSoldToType('');
      setSoldTo('');
      setTenantPhone('');
      setTenantEmail('');
      setMoveInDate('');
      fetchProperties();
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setMarkingSold(false);
    }
  };

  // Fetch PG Occupants
  const fetchPgOccupants = useCallback(async (propertyId) => {
    setLoadingPgBeds(true);
    try {
      const res = await getPgOccupants(propertyId);
      if (res.status) {
        setPgOccupants(res.data);
      } else {
        toast.error('Failed to load bed occupants');
      }
    } catch (e) {
      toast.error('Failed to load bed occupants');
    } finally {
      setLoadingPgBeds(false);
    }
  }, []);

  // Bed Assign
  const handleAssignBed = async () => {
    if (!pgBedModal || !assigningBed) return;
    setBedAssigning(true);
    try {
      const formData = new FormData();
      formData.append('property_id', pgBedModal.id);
      formData.append('user_id', pgBedModal.user_id || 2);
      formData.append('room_id', assigningBed.roomId);
      formData.append('bed_number', assigningBed.bedNumber);

      let selectedUser = null;
      if (bedAssignType !== 'others' && bedAssignType !== '') {
        selectedUser = appUsers.find(u => String(u.id) === String(bedAssignType));
      }

      if (selectedUser) {
        formData.append('occupant_user_id', selectedUser.id);
      } else {
        formData.append('occupant_name', bedAssignName);
        formData.append('occupant_phone', bedAssignPhone);
        formData.append('occupant_email', bedAssignEmail);
      }

      if (bedAssignMoveIn) {
        formData.append('move_in_date', bedAssignMoveIn);
      }

      const res = await addPgBedOccupant(formData);
      if (res.status) {
        toast.success(`Bed assigned successfully`);
        setAssigningBed(null);
        setBedAssignType('');
        setBedAssignName('');
        setBedAssignPhone('');
        setBedAssignEmail('');
        setBedAssignMoveIn('');
        await fetchPgOccupants(pgBedModal.id);
      } else {
        toast.error(res.message || 'Failed to assign bed');
      }
    } catch (e) {
      toast.error(e.message || 'Failed to assign bed');
    } finally {
      setBedAssigning(false);
    }
  };

  // Vacate Bed
  const handleVacateBed = async (roomId, bedNumber) => {
    if (!window.confirm(`Vacate bed ${bedNumber}?`)) return;
    try {
      const res = await vacatePgBed(roomId, bedNumber);
      if (res.status) {
        toast.success('Bed vacated');
        if (pgBedModal) await fetchPgOccupants(pgBedModal.id);
      } else {
        toast.error(res.message || 'Failed to vacate bed');
      }
    } catch (e) {
      toast.error('Failed to vacate bed');
    }
  };

  // Status toggle
  const handleStatusChange = async (prop, newStatus) => {
    try {
      await updateProperty(prop.id, { property_id: prop.id, status: newStatus });
      toast.success(`Status changed to ${newStatus}`);
      fetchProperties();
    } catch (err) {
      toast.error(err.message || 'Failed');
    }
  };

  // Mark Available
  const handleMarkAvailable = async (prop) => {
    try {
      await updateProperty(prop.id, { property_id: prop.id, status: 'active', sold_to: null });
      toast.success('Property marked as available');
      fetchProperties();
    } catch (err) {
      toast.error(err.message || 'Failed to update status');
    }
  };

  return (
    <div className="p-6 lg:p-8 flex flex-col h-[calc(100vh-theme(spacing.16))] lg:h-screen">
      <Helmet>
        <title>Properties | JoldiPabo Admin</title>
      </Helmet>
      {/* Header */}
      <div className="flex flex-col gap-3 mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Properties
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">{total} listings found as per filters</p>
        </div>
        <div className="flex gap-2 items-center">
          <form onSubmit={handleSearchSubmit} className="relative w-full sm:w-64 hidden sm:block mr-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              className="h-9 pl-9 text-sm"
              placeholder="Search by title, owner, city..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>

          <Button
            variant={activeFiltersCount > 0 ? "default" : "outline"}
            size="sm"
            onClick={handleOpenFilterModal}
            className={`gap-1.5 h-9 ${activeFiltersCount > 0 ? 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm' : ''}`}
          >
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {activeFiltersCount > 0 && (
              <span className="ml-1 px-1.5 py-0.5 bg-white/20 text-white rounded-full text-xs font-semibold">
                {activeFiltersCount}
              </span>
            )}
          </Button>
          <Button variant="outline" size="sm" onClick={fetchProperties} className="gap-1.5 h-9">
            <RefreshCw className="w-4 h-4" /> Refresh
          </Button>
        </div>
      </div>

      {/* Mobile Search (visible only on small screens) */}
      <div className="sm:hidden mb-4">
        <form onSubmit={handleSearchSubmit} className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            className="h-10 pl-9 text-sm"
            placeholder="Search by title, owner, city..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </form>
      </div>



      {/* Filter Modal Popup */}
      <Dialog open={isFilterModalOpen} onOpenChange={setIsFilterModalOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] flex flex-col p-0 overflow-hidden shadow-2xl rounded-2xl border border-slate-200">
          {/* Header */}
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between bg-slate-50/80">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-orange-100 text-[#ee7923] flex items-center justify-center font-semibold shadow-xs">
                <SlidersHorizontal className="w-4 h-4" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-slate-900 leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Filter Properties
                </DialogTitle>
                <DialogDescription className="text-[11px] text-slate-500 mt-0.5">
                  Refine properties by type, configuration, price, and location
                </DialogDescription>
              </div>
            </div>
            {tempActiveCount > 0 && (
              <button
                type="button"
                onClick={handleResetTempFilters}
                className="text-xs font-semibold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-2.5 py-1 rounded-lg flex items-center gap-1 transition-colors mr-6"
              >
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            )}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-3.5 space-y-4">
            {/* Location */}
            <div>
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2 block">Location</Label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <span className="text-[11px] text-slate-500 mb-1 block font-medium">State</span>
                  <ReactSelect
                    className="text-xs"
                    options={[
                      { value: '', label: 'All States' },
                      ...INDIAN_STATES.map(s => ({ value: s, label: s }))
                    ]}
                    value={
                      tempFilters.state
                        ? { value: tempFilters.state, label: tempFilters.state }
                        : { value: '', label: 'All States' }
                    }
                    onChange={opt => setTempFilters(prev => ({ ...prev, state: opt?.value || '' }))}
                    isSearchable={true}
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        height: '38px',
                        borderRadius: '0.75rem',
                        fontSize: '12px',
                        borderColor: '#e2e8f0',
                        backgroundColor: '#f8fafc',
                        '&:hover': { borderColor: '#cbd5e1' }
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999, fontSize: '12px' }),
                      option: (base) => ({
                        ...base,
                        fontSize: '12px',
                        padding: '6px 12px'
                      })
                    }}
                  />
                </div>
                <div>
                  <span className="text-[11px] text-slate-500 mb-1 block font-medium">District</span>
                  <ReactSelect
                    className="text-xs"
                    options={[
                      { value: '', label: 'All Districts' },
                      ...districtsList.map(d => ({ value: d, label: d }))
                    ]}
                    value={
                      tempFilters.district
                        ? { value: tempFilters.district, label: tempFilters.district }
                        : { value: '', label: 'All Districts' }
                    }
                    onChange={opt => setTempFilters(prev => ({ ...prev, district: opt?.value || '' }))}
                    isSearchable={true}
                    placeholder="Select District"
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: '38px',
                        height: '38px',
                        borderRadius: '0.75rem',
                        fontSize: '12px',
                        borderColor: '#e2e8f0',
                        backgroundColor: '#f8fafc',
                        '&:hover': { borderColor: '#cbd5e1' }
                      }),
                      menu: (base) => ({ ...base, zIndex: 9999, fontSize: '12px' }),
                      option: (base) => ({
                        ...base,
                        fontSize: '12px',
                        padding: '6px 12px'
                      })
                    }}
                  />
                </div>
                <div>
                  <span className="text-[11px] text-slate-500 mb-1 block font-medium">City / Area</span>
                  <Input
                    className="h-[38px] text-xs rounded-xl border-slate-200 bg-slate-50/50 focus:bg-white transition-all"
                    placeholder="e.g. Purana Bazaar"
                    value={tempFilters.city}
                    onChange={e => setTempFilters(prev => ({ ...prev, city: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Property Type */}
            <div className="pt-2 border-t border-slate-100">
              <div className="flex items-center justify-between mb-2.5">
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider">Property Type</Label>
                {tempFilters.property_type && (
                  <span className="text-xs text-[#ee7923] font-semibold">{tempFilters.property_type}</span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTempFilters(prev => ({ ...prev, property_type: '' }))}
                  className={`px-3.5 py-1.5 text-xs rounded-xl font-medium transition-all ${!tempFilters.property_type
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                    }`}
                >
                  All Types
                </button>
                {PROPERTY_TYPES.map(t => {
                  const isSelected = tempFilters.property_type === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setTempFilters(prev => ({ ...prev, property_type: isSelected ? '' : t }))}
                      className={`px-3.5 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                        }`}
                    >
                      {t}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Listing Type & Status in 2 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
              {/* Listing Type */}
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">Listing Type</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'All', value: '' },
                    { label: 'For Sale', value: 'sell' },
                    { label: 'For Rent', value: 'rent' }
                  ].map(item => {
                    const isSelected = tempFilters.listing_type === item.value;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => setTempFilters(prev => ({ ...prev, listing_type: item.value }))}
                        className={`px-3.5 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                          }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Status */}
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">Listing Status</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'All', value: '' },
                    { label: 'Active', value: 'active' },
                    { label: 'Inactive', value: 'inactive' },
                    { label: 'Sold', value: 'sold' },
                    { label: 'Rented', value: 'rented' }
                  ].map(item => {
                    const isSelected = tempFilters.status === item.value;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => setTempFilters(prev => ({ ...prev, status: item.value }))}
                        className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                          }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* BHK / Size */}
            <div className="pt-2 border-t border-slate-100">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">BHK Configuration</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTempFilters(prev => ({ ...prev, bhk: '' }))}
                  className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${!tempFilters.bhk
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                    }`}
                >
                  All
                </button>
                {BHK_OPTIONS.map(b => {
                  const isSelected = tempFilters.bhk === b;
                  return (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setTempFilters(prev => ({ ...prev, bhk: isSelected ? '' : b }))}
                      className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                        }`}
                    >
                      {b}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Furnishing & Fooding in 2 columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2 border-t border-slate-100">
              {/* Furnishing */}
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">Furnishing</Label>
                <div className="flex flex-wrap gap-2">
                  {['All', ...FURNISHING_OPTIONS].map(f => {
                    const val = f === 'All' ? '' : f;
                    const isSelected = tempFilters.furnishing === val;
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setTempFilters(prev => ({ ...prev, furnishing: val }))}
                        className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                          }`}
                      >
                        {f}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Food Facility (PG) */}
              <div>
                <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">Food / Mess (PG)</Label>
                <div className="flex flex-wrap gap-2">
                  {[
                    { label: 'All', value: '' },
                    { label: 'Food Included', value: '1' },
                    { label: 'Self Cooking', value: '0' }
                  ].map(item => {
                    const isSelected = tempFilters.serves_food === item.value;
                    return (
                      <button
                        key={item.label}
                        type="button"
                        onClick={() => setTempFilters(prev => ({ ...prev, serves_food: item.value }))}
                        className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                          ? 'bg-slate-900 text-white shadow-sm'
                          : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                          }`}
                      >
                        {item.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Price Range */}
            <div className="pt-2 border-t border-slate-100">
              <Label className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2.5 block">Price Range</Label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setTempFilters(prev => ({ ...prev, price_range: '' }))}
                  className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${!tempFilters.price_range
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                    }`}
                >
                  Any Price
                </button>
                {PRICE_RANGE_OPTIONS.map(p => {
                  const isSelected = tempFilters.price_range === p.value;
                  return (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setTempFilters(prev => ({ ...prev, price_range: isSelected ? '' : p.value }))}
                      className={`px-3 py-1.5 text-xs rounded-xl font-medium transition-all ${isSelected
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200/80 border border-slate-200/60'
                        }`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-slate-100 bg-slate-50/80 flex items-center justify-between">
            <Button
              type="button"
              variant="ghost"
              onClick={handleResetTempFilters}
              className="text-slate-500 hover:text-slate-800 text-xs gap-1.5"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Clear All
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsFilterModalOpen(false)}
                className="text-xs h-9"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleApplyFilters}
                className="bg-slate-900 hover:bg-slate-800 text-white text-xs h-9 gap-1.5 shadow-sm"
              >
                Apply Filters
                {tempActiveCount > 0 && ` (${tempActiveCount})`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Properties Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="w-8 h-8 border-4 border-[#ee7923] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : properties.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">No properties found</p>
          <p className="text-sm mt-1">Try changing filters or add new listings</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {properties.map(prop => {
            const isUnavailable = prop.status !== 'active';
            return (
              <Card key={prop.id} className="overflow-hidden border border-slate-200 shadow-sm hover:shadow-md transition-shadow">
                {/* Image */}
                <div className="relative h-44 bg-slate-200 overflow-hidden">
                  <div className={`w-full h-full ${isUnavailable ? 'opacity-40 grayscale-[30%]' : 'bg-slate-100'}`}>
                    {prop.images && prop.images.length > 0 ? (
                      <img src={resolveImageUrl(prop.images[0], 'property_images')} alt={prop.property_title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-400 bg-slate-100">
                        <Home className="w-12 h-12" />
                      </div>
                    )}
                  </div>

                  {isUnavailable && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                      <div className="bg-slate-900/80 text-white font-bold tracking-widest px-4 py-1.5 rounded shadow-lg uppercase">
                        {prop.status === 'inactive' ? 'DEACTIVATED' : prop.status}
                      </div>
                    </div>
                  )}

                  <div className="absolute top-2 left-2 flex gap-1.5 z-20">
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${LISTING_BADGE[prop.listing_type] || LISTING_BADGE.sell}`}>
                      {prop.listing_type === 'rent' ? 'RENT' : 'SELL'}
                    </span>
                  </div>
                  {(role === 'admin' || role === 'data_entry') && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleDelete(prop); }}
                      disabled={deletingId === prop.id}
                      className="absolute top-2 right-2 w-8 h-8 bg-red-600 hover:bg-red-700 text-white rounded-full flex items-center justify-center shadow-md hover:scale-105 transition-all z-10 disabled:opacity-40"
                      title="Delete property"
                    >
                      <Trash2 className="w-4 h-4 fill-white text-white" />
                    </button>
                  )}
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 text-sm truncate">{prop.property_title}</h3>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">
                    {[prop.address, prop.city, prop.district, prop.state].filter(Boolean).join(', ')}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[#ee7923] font-bold text-base">{fmt(prop.price)}</span>
                    {prop.listing_type === 'rent' && <span className="text-xs text-slate-400">/month</span>}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {[prop.property_type, prop.bhk, prop.furnishing, prop.area_sqft ? `${prop.area_sqft} sqft` : ''].filter(Boolean).join(' · ')}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Owner: <span className="font-medium text-slate-700">{prop.owner_name}</span> · {prop.owner_phone}
                  </p>

                  {prop.status === 'sold' && prop.sold_to && (
                    <p className="text-xs text-emerald-700 mt-1 font-medium">Sold to: {prop.sold_to}</p>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100 flex-wrap">
                    {/* View */}
                    <button
                      onClick={() => setSelectedProperty(prop)}
                      className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium"
                    >
                      <Eye className="w-3 h-3" /> View
                    </button>

                    {/* Edit */}
                    <button
                      onClick={() => openEdit(prop)}
                      className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-[#2D9CDB] text-[#2D9CDB] hover:bg-sky-50 font-medium"
                    >
                      <Pencil className="w-3 h-3" /> Edit
                    </button>

                    {/* Status toggle */}
                    {prop.status === 'active' && (
                      <button
                        onClick={() => handleStatusChange(prop, 'inactive')}
                        className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-amber-600 text-amber-600 hover:bg-amber-50 font-medium"
                      >
                        Deactivate
                      </button>
                    )}
                    {prop.status === 'inactive' && (
                      <button
                        onClick={() => handleStatusChange(prop, 'active')}
                        className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-[#27AE60] text-[#27AE60] hover:bg-emerald-50 font-medium"
                      >
                        Activate
                      </button>
                    )}

                    {/* Mark Sold/Rented OR Mark Available */}
                    {(prop.status === 'sold' || prop.status === 'rented') ? (
                      <button
                        onClick={() => handleMarkAvailable(prop)}
                        className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-[#f48222] text-[#f48222] hover:bg-orange-50 font-medium"
                      >
                        <RotateCcw className="w-3 h-3" /> Mark as Available
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          if (prop.property_type === 'PG' && prop.listing_type === 'rent') {
                            setPgBedModal(prop);
                            fetchPgOccupants(prop.id);
                          } else {
                            setMarkSoldModal(prop);
                            setSoldToType('');
                            setSoldTo('');
                          }
                        }}
                        className="flex items-center gap-1 h-8 px-3 text-xs rounded-lg border border-[#27AE60] text-[#27AE60] hover:bg-emerald-50 font-medium"
                      >
                        {prop.listing_type === 'rent' ? 'Mark Rented' : 'Mark Sold'}
                      </button>
                    )}

                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
            Prev
          </Button>
          <span className="text-sm text-slate-600">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages}>
            Next
          </Button>
        </div>
      )}

      {/* Mark Sold Modal */}
      <Dialog open={Boolean(markSoldModal)} onOpenChange={open => { if (!open) setMarkSoldModal(null); }}>
        <DialogContent className="bg-white max-w-sm overflow-visible" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {markSoldModal?.listing_type === 'rent' ? 'Mark as Rented' : 'Mark as Sold'}
            </DialogTitle>
            <DialogDescription>
              {markSoldModal?.property_title}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>{markSoldModal?.listing_type === 'rent' ? 'Rented To' : 'Sold To'} <span className="text-slate-400 text-xs">(optional)</span></Label>
            <ReactSelect
              className="mt-1.5"
              options={[
                { value: '', label: '-- Select User --' },
                ...appUsers.map(u => ({ value: String(u.id), label: `${u.name} (${u.phone})` })),
                { value: 'others', label: 'Others (Manual Entry)' }
              ]}
              value={
                soldToType
                  ? (soldToType === 'others'
                    ? { value: 'others', label: 'Others (Manual Entry)' }
                    : { value: String(soldToType), label: appUsers.find(u => String(u.id) === String(soldToType)) ? `${appUsers.find(u => String(u.id) === String(soldToType)).name} (${appUsers.find(u => String(u.id) === String(soldToType)).phone})` : soldToType })
                  : { value: '', label: '-- Select User --' }
              }
              onChange={selectedOption => setSoldToType(selectedOption?.value || '')}
              isClearable={false}
              isSearchable={true}
              styles={{
                menuPortal: base => ({ ...base, zIndex: 9999 }),
                menu: base => ({ ...base, zIndex: 9999 }),
              }}
            />

            {soldToType === 'others' && (
              <div className="space-y-3 mt-3">
                <Input
                  className="h-11"
                  placeholder="Enter name manually..."
                  value={soldTo}
                  onChange={e => setSoldTo(e.target.value)}
                />
                <Input
                  className="h-11"
                  placeholder="Enter phone number..."
                  value={tenantPhone}
                  onChange={e => setTenantPhone(e.target.value)}
                />
                <Input
                  className="h-11"
                  placeholder="Enter email (optional)..."
                  value={tenantEmail}
                  onChange={e => setTenantEmail(e.target.value)}
                />
              </div>
            )}

            <div className="mt-4">
              <Label>{markSoldModal?.listing_type === 'rent' ? 'Move-in Date' : 'Handover / Move-in Date'} <span className="text-slate-400 text-xs">(optional)</span></Label>
              <Input
                type="date"
                className="mt-1.5 h-11"
                value={moveInDate}
                onChange={e => setMoveInDate(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMarkSoldModal(null)}>Cancel</Button>
            <Button onClick={handleMarkSold} disabled={markingSold} className="bg-slate-900 hover:bg-slate-800 text-white">
              {markingSold ? 'Processing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* PG Bed Modal */}
      <Dialog open={Boolean(pgBedModal)} onOpenChange={open => { if (!open) setPgBedModal(null); }}>
        <DialogContent className="bg-white max-w-3xl overflow-visible max-h-[85vh] overflow-y-auto scrollbar-hide" onInteractOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Manage PG Beds</DialogTitle>
            <DialogDescription>{pgBedModal?.property_title}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            {loadingPgBeds ? (
              <p className="text-center text-sm text-slate-500 py-4">Loading beds...</p>
            ) : (
              pgBedModal?.rooms?.map((room, roomIdx) => (
                <div key={room.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="mb-3">
                    <h3 className="font-semibold text-slate-800">Room {roomIdx + 1} <span className="text-sm font-normal text-slate-500">({room.room_type} - {room.num_beds} Bed{room.num_beds > 1 ? 's' : ''})</span></h3>
                    <p className="text-xs text-slate-500 mt-1 flex items-center space-x-2">
                      <span>{Number(room.ac) === 1 ? 'AC' : 'Non-AC'}</span>
                      <span>•</span>
                      <span>{Number(room.attached_washroom) === 1 ? 'Attached Washroom' : 'Common Washroom'}</span>
                      <span>•</span>
                      <span className="font-medium text-slate-700">{fmt(room.price_per_bed)} / bed</span>
                    </p>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Array.from({ length: room.num_beds }).map((_, i) => {
                      const bedNumber = i + 1;
                      const occupant = pgOccupants.find(o => String(o.room_id) === String(room.id) && String(o.bed_number) === String(bedNumber) && o.status === 'occupied');

                      return (
                        <div key={bedNumber} className={`p-3 rounded-md border ${occupant ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <span className="font-medium text-sm text-slate-700">Bed #{bedNumber}</span>
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${occupant ? 'bg-orange-100 text-orange-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {occupant ? 'Occupied' : 'Available'}
                            </span>
                          </div>
                          {occupant && <hr className="border-black/5 mb-2" />}

                          {occupant ? (
                            <div className="text-xs space-y-1.5">
                              <p className="font-medium text-slate-800 line-clamp-1 mb-1">{occupant.occupant_name}</p>
                              <div className="flex items-center text-black">
                                <Phone className="w-3 h-3 mr-1.5 text-slate-500" />
                                <span>{occupant.occupant_phone}</span>
                              </div>
                              {occupant.move_in_date && (
                                <div className="flex items-center text-black">
                                  <Calendar className="w-3 h-3 mr-1.5 text-slate-500" />
                                  <span>Moved in: {occupant.move_in_date}</span>
                                </div>
                              )}
                              <Button size="sm" className="h-6 text-[10px] mt-2 w-full bg-red-500 text-white hover:bg-red-500 hover:text-white" onClick={() => handleVacateBed(room.id, bedNumber)}>Vacate</Button>
                            </div>
                          ) : (
                            <Button size="sm" className="h-6 text-[10px] mt-2 w-full bg-slate-900 text-white hover:bg-slate-900 hover:text-white" onClick={() => setAssigningBed({ roomId: room.id, bedNumber })}>Assign</Button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
            {!loadingPgBeds && (!pgBedModal?.rooms || pgBedModal.rooms.length === 0) && (
              <p className="text-center text-sm text-slate-500 py-4">No rooms found for this property.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Bed Modal */}
      <Dialog open={Boolean(assigningBed)} onOpenChange={open => { if (!open) setAssigningBed(null); }}>
        <DialogContent className="bg-white max-w-sm overflow-visible">
          <DialogHeader>
            <DialogTitle>Assign Bed {assigningBed?.bedNumber}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label>Assign To <span className="text-slate-400 text-xs">(optional)</span></Label>
              <ReactSelect
                className="mt-1.5"
                options={[
                  { value: '', label: '-- Select User --' },
                  ...appUsers.map(u => ({ value: String(u.id), label: `${u.name} (${u.phone})` })),
                  { value: 'others', label: 'Others (Manual Entry)' }
                ]}
                value={
                  bedAssignType
                    ? (bedAssignType === 'others'
                      ? { value: 'others', label: 'Others (Manual Entry)' }
                      : { value: String(bedAssignType), label: appUsers.find(u => String(u.id) === String(bedAssignType)) ? `${appUsers.find(u => String(u.id) === String(bedAssignType)).name} (${appUsers.find(u => String(u.id) === String(bedAssignType)).phone})` : bedAssignType })
                    : { value: '', label: '-- Select User --' }
                }
                onChange={selectedOption => setBedAssignType(selectedOption?.value || '')}
              />
            </div>

            {bedAssignType === 'others' && (
              <div className="space-y-3">
                <Input placeholder="Enter name manually..." value={bedAssignName} onChange={e => setBedAssignName(e.target.value)} />
                <Input placeholder="Enter phone number..." value={bedAssignPhone} onChange={e => setBedAssignPhone(e.target.value)} />
                <Input placeholder="Enter email (optional)..." value={bedAssignEmail} onChange={e => setBedAssignEmail(e.target.value)} />
              </div>
            )}

            <div>
              <Label>Move-in Date</Label>
              <Input type="date" className="mt-1.5 h-11" value={bedAssignMoveIn} onChange={e => setBedAssignMoveIn(e.target.value)} />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAssigningBed(null)}>Cancel</Button>
            <Button onClick={handleAssignBed} disabled={bedAssigning} className="bg-slate-900 hover:bg-slate-800 text-white">
              {bedAssigning ? 'Saving...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property Detail Side Sheet */}
      {selectedProperty && (
        <PropertyDetail
          property={selectedProperty}
          onClose={() => setSelectedProperty(null)}
          onRefresh={fetchProperties}
        />
      )}
    </div>
  );
};

export default PropertiesPage;
